// InlineDiff — the element-level structural diff, pure. Composes DiffMark.
//
// .claude/research/design/03: a proposal renders as a diff inside the material, at the
// element it changes (Google-Docs suggesting-mode shape). Because the material
// is a typed artifact, the diff is at element/field grain, not text lines
// (merge-editor-ux.md) — so this component is a match over the `DiffOp`
// discriminant, one render per op:
//   • scalar-edit / list-edit → the old value struck + the new value inserted,
//     COMPOSING DiffMark (which owns the colour-never-alone rule: glyph +
//     underline/strike). Field-grain, rendered from the op's before/after.
//   • inline-edit → the same struck-then-inserted shape, but each side is a
//     typed RichText tree rendered by RichText, so the diff shows at MARK grain
//     (a changed emphasis/strong node), not as a changed text line.
//   • add    → the new element body (passed as `children`) wrapped in an added
//     treatment (add tint rail + "+" + a spelled-out "Added" for AT).
//   • remove → the existing body struck/dimmed with a "−" + "Removed".
//   • move   → a compact "Moved from X to Y" indicator (no text diff).
//
// Colour is never the only channel anywhere here: DiffMark carries glyph +
// decoration for the field edits, and the whole-element cases carry a glyph + a
// VisuallyHidden word. The op payload carries exactly what each case needs, so
// there is no flag and no raw object threading through.
//
// Pure/props-driven: `op` selects the render; `children` is the element body,
// used only by the add/remove cases (an edit renders from its own before/after,
// a move from its labels). No state, no I/O, no domain knowledge.

import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { DiffMark } from "../../DiffMark/DiffMark.js";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import { RichText } from "../RichText/RichText.js";
import type { DiffOp, RichText as RichTextTree } from "../types.js";
import "./InlineDiff.css";

export type InlineDiffProps = {
    readonly op: DiffOp;
    // The rendered element body — used only by the whole-element add/remove
    // cases, which wrap it in their treatment. Ignored by edits and moves.
    readonly children?: ReactNode;
};

// The shared field-edit render: old struck, new inserted, via DiffMark. Both
// scalar and list edits diff the same way inline — they differ only in which
// review OUTCOMES are legal (ScalarEditOp has no "Keep both"), which is the
// ReviewControl's concern, not the diff's.
const FieldEdit = ({ before, after }: { before: string; after: string }) => (
    <span className="bilda-inline-diff__field">
        <DiffMark variant="remove">{before}</DiffMark>{" "}
        <DiffMark variant="add">{after}</DiffMark>
    </span>
);

// The node-grain inline edit: same struck-then-inserted shape as FieldEdit, but
// each side is a typed RichText tree rendered by RichText (so a mark change like
// "*estate* → **estate**" is visible AS a mark change), not an opaque string.
const InlineEdit = ({
    before,
    after,
}: {
    before: RichTextTree;
    after: RichTextTree;
}) => (
    <span className="bilda-inline-diff__field">
        <DiffMark variant="remove">
            <RichText content={before} />
        </DiffMark>{" "}
        <DiffMark variant="add">
            <RichText content={after} />
        </DiffMark>
    </span>
);

export const InlineDiff = ({ op, children }: InlineDiffProps): ReactNode => {
    switch (op.type) {
        case "scalar-edit":
        case "list-edit":
            return <FieldEdit before={op.before} after={op.after} />;
        case "inline-edit":
            return <InlineEdit before={op.before} after={op.after} />;
        case "add":
            return (
                <div className="bilda-inline-diff bilda-inline-diff--add">
                    <VisuallyHidden>
                        <Trans>Added:</Trans>{" "}
                    </VisuallyHidden>
                    <span
                        className="bilda-inline-diff__glyph"
                        aria-hidden="true"
                    >
                        +
                    </span>
                    <div className="bilda-inline-diff__body">{children}</div>
                </div>
            );
        case "remove":
            return (
                <div className="bilda-inline-diff bilda-inline-diff--remove">
                    <VisuallyHidden>
                        <Trans>Removed:</Trans>{" "}
                    </VisuallyHidden>
                    <span
                        className="bilda-inline-diff__glyph"
                        aria-hidden="true"
                    >
                        −
                    </span>
                    <div className="bilda-inline-diff__body">{children}</div>
                </div>
            );
        case "move":
            return (
                <div className="bilda-inline-diff bilda-inline-diff--move">
                    <span
                        className="bilda-inline-diff__glyph"
                        aria-hidden="true"
                    >
                        ⇅
                    </span>
                    <span className="bilda-inline-diff__move-text">
                        <Trans>
                            Moved from {op.from} to {op.to}
                        </Trans>
                    </span>
                </div>
            );
        default: {
            // Exhaustiveness: a new DiffOp variant fails to compile here.
            const exhaustive: never = op;
            return exhaustive;
        }
    }
};
